import React, { useRef } from 'react'
import { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import { CheckPicker, PickerHandle, SelectPicker } from 'rsuite'
import { useFetchAllCouriers } from '@@partner:graphql/query/employees/allCouriers.gql'
import { Courier } from '@@partner:types:app'
import { COURIER } from '@hive/status'

interface BaseProps {
  isMultiple: boolean
  filter?: NestedFilter
}

interface MultipleProps extends BaseProps {
  isMultiple: true
  value: GQL_ID[]
  onChange: (value: GQL_ID[]) => void
}

interface SingleProps extends BaseProps {
  isMultiple: false
  value: GQL_ID
  onChange: (value: GQL_ID, data?: Courier) => void
}

type Props = MultipleProps | SingleProps

const prepareValue = (couriers?: Courier[]): {
  label: string,
  value: GQL_ID
}[] => {
  if (!couriers) return []

  return couriers.map(courier => ({
    label: `${courier.first_name} ${courier.last_name}`,
    value: courier.id
  }))
}

export default function SelectCourierPicker ({
  value,
  onChange,
  isMultiple = false,
  showDisabled = false,
  filter = {}
}: Props) {
  const ref = useRef<PickerHandle>(null)
  const [ searchText, setSearchText ] = React.useState('')

  const { data, loading, error } = useFetchAllCouriers({
    filter: searchText && searchText.length > 0 ? {
      or: [
        {
          fields: [ 'first_name', 'last_name' ],
          op: 'includes',
          value: [ searchText ]
        },
        {
          fields: [ 'id' ],
          op: 'contains',
          value: Array.isArray(value) ? value : [ value ]
        }
      ]
    } : {
      and: [
        filter,
        {
          fields: [ 'status' ],
          op: showDisabled ? 'unwrap' : 'eq',
          value: COURIER.FREE
        }
      ]},
    pager: {
      page: 1,
      limit: 20
    }
  })

  const { data: selectedCouriers } = useFetchAllCouriers({
    filter: {
      fields: [ 'id' ],
      op: 'contains',
      value: Array.isArray(value) ? value : [ value ]
    },
    pager: {
      page: 1,
      limit: 20
    }
  })

  const mergedCouriers = [ ...(selectedCouriers?.couriers.edges || []), ...(data?.couriers.edges || []) ].reduce((acc, courier) => {
    if (!acc.some(c => c.id === courier.id)) {
      acc.push(courier)
    }
    return acc
  }, [] as Courier[])

  const handleSearch = (search: string) => {
    setSearchText(search)
  }

  if (isMultiple) {
    return (
      <CheckPicker
        ref={ref}
        value={value}
        data={prepareValue(mergedCouriers)}
        onChange={onChange}
        searchable={true}
        onSearch={handleSearch}
        placeholder="Все курьеры"
        valueKey="value"
        labelKey="label"
      />
    )
  }

  return (
    <SelectPicker
      ref={ref}
      value={value}
      data={prepareValue(mergedCouriers)}
      onChange={onChange}
      loading={loading}
      searchable={true}
      onSearch={handleSearch}
      placeholder="Все курьеры"
      valueKey="value"
      labelKey="label"
    />
  )
}